// packages/web-ui/src/state/sse.ts
//
// W2-S5 AC #10 — `sse` zustand slice. Tracks SSE connection lifecycle
// (`disconnected` → `connecting` → `connected` → `reconnecting` → ...),
// `lastEventId` for `since=` replay, the current attempt counter, and the
// session id we are subscribed to.
//
// Pure state slice. The actual fetch-event-source plumbing lives in
// `<SseProvider>` + `client.ts`; this slice records the lifecycle so the
// `<ReconnectingBanner>` and the `chat` slice can react.

import { createStore } from 'zustand/vanilla';
import type { StateCreator } from 'zustand';

export type SseConnectionState =
  | 'disconnected'
  | 'connecting'
  | 'connected'
  | 'reconnecting';

export interface SseSliceState {
  connectionState: SseConnectionState;
  lastEventId: string | null;
  reconnectAttempts: number;
  currentSessionId: string | null;
  /** True when the slice gave up retrying (10 attempts) — user must click Retry. */
  exhausted: boolean;
}

export const SSE_INITIAL_STATE: SseSliceState = {
  connectionState: 'disconnected',
  lastEventId: null,
  reconnectAttempts: 0,
  currentSessionId: null,
  exhausted: false,
};

export interface SseSliceActions {
  /** Mark transition to `connecting` for a session. */
  beginConnect: (sessionId: string) => void;
  /** Mark transition to `connected` and reset attempts/exhausted. */
  markOpen: () => void;
  /** Increment attempts; mark `reconnecting` if not exhausted; mark exhausted otherwise. */
  markReconnecting: () => void;
  /** Force the slice into `disconnected`. */
  markDisconnected: () => void;
  /** Reset attempts and exhausted (called when user explicitly clicks Retry). */
  resetAttempts: () => void;
  /**
   * Persist the most-recently-dispatched event id for replay.
   * Pass `null` to clear the cursor — needed on session switch so the
   * server's per-session ring replays from 0 instead of from the previous
   * session's (higher) global event id.
   */
  setLastEventId: (id: string | null) => void;
  /** Record the active session id (mirror of chat slice's `activeSessionId`). */
  setCurrentSession: (sessionId: string | null) => void;
  _resetSse: () => void;
}

export type SseSlice = SseSliceState & SseSliceActions;

/** Maximum consecutive reconnect attempts before giving up (FR-SHELL-8 + AC #10). */
export const MAX_RECONNECT_ATTEMPTS = 10;

export const createSseSlice: StateCreator<SseSlice, [], [], SseSlice> = (set, _get) => ({
  ...SSE_INITIAL_STATE,

  beginConnect: (sessionId) => {
    set((s) => ({
      ...s,
      connectionState: 'connecting',
      currentSessionId: sessionId,
    }));
  },

  markOpen: () => {
    set((s) => ({
      ...s,
      connectionState: 'connected',
      reconnectAttempts: 0,
      exhausted: false,
    }));
  },

  markReconnecting: () => {
    set((s) => {
      const next = s.reconnectAttempts + 1;
      if (next > MAX_RECONNECT_ATTEMPTS) {
        return {
          ...s,
          connectionState: 'disconnected',
          reconnectAttempts: next,
          exhausted: true,
        };
      }
      return {
        ...s,
        connectionState: 'reconnecting',
        reconnectAttempts: next,
        exhausted: false,
      };
    });
  },

  markDisconnected: () => {
    set((s) => ({
      ...s,
      connectionState: 'disconnected',
    }));
  },

  resetAttempts: () => {
    set((s) => ({ ...s, reconnectAttempts: 0, exhausted: false }));
  },

  setLastEventId: (id) => {
    set((s) => ({ ...s, lastEventId: id }));
  },

  setCurrentSession: (sessionId) => {
    set((s) => ({ ...s, currentSessionId: sessionId }));
  },

  _resetSse: () => {
    set({ ...SSE_INITIAL_STATE } as SseSlice);
  },
});

/** Vanilla store factory for tests/non-shell call-sites. */
export function createSseStore() {
  return createStore<SseSlice>()(createSseSlice);
}

// ─── Selectors ───────────────────────────────────────────────────────────

export function selectSseConnectionState(s: SseSliceState): SseConnectionState {
  return s.connectionState;
}

export function selectReconnectAttempts(s: SseSliceState): number {
  return s.reconnectAttempts;
}

export function selectSseExhausted(s: SseSliceState): boolean {
  return s.exhausted;
}
